import { Injectable } from '@angular/core';
import {
  MessageService,
  WindowService,
  PhoneNumberService,
  ToastService,
  ToastStyle,
  LoggerService,
} from '@wephone-utils';
import { AuthenticationService } from '@wephone-core/service/authentication';
import { WsRpcService } from '@wephone-core/service/wsrpc_service';
import { _ti } from '@wephone-translation';
import { SmsService, SmsDialogService } from '@wephone-sms/service';
import { SmsDialog, SmsMessage } from '@wephone-sms/sms.types';
import * as _ from 'lodash';
import { WebPushService } from '@wephone-core/service/webpush.service';
import { SwPush } from '@angular/service-worker';
import { environment } from 'src/environments/environment';
import { EnterpriseRepository } from '@wephone-core/model/repository/enterprise';
import { FlexIvrSettings, ConfigManager } from '@wephone-core/wephone-core.module';
import { NotificationService } from '@wephone-core/service/notification.service';
import { VMReaderDataMsg } from '@wephone-core/model/repository/voicemail.i';
import { EnterpriseParamDict, FeatureName } from '@wephone-core/system';

@Injectable()
export class PhoneConfigService {

  constructor(
    private readonly messageService: MessageService,
    private readonly auth: AuthenticationService,
    private readonly sms: SmsService,
    private readonly smsDialogService: SmsDialogService,
    private readonly wsrpc: WsRpcService,
    private readonly windowService: WindowService,
    private readonly toast: ToastService,
    private readonly webpushService: WebPushService,
    private readonly swPush: SwPush,
    private readonly settings: FlexIvrSettings,
    private readonly configService: ConfigManager,
    private readonly loggerService: LoggerService,
  ) {
  }

  init(): void {
    if (this.auth.isAuthenticated()) {
      this.initPhoneApp();
    } else {
      this.messageService.subscribe('application:login:success',
        () => {
          this.initPhoneApp();
        }
      );
    }
  }

  private initPhoneApp(): void {
    if (this.configService.hasFeature(FeatureName.SMS)) {
      this.sms.updateTotalConversationUnreadSms();
    }

    this.subscribeNewSms();
    this.subscribeStatusSms();
    this.subscribeQualificationDone();
    this.subscribeDisconnectAgent();
    this.subscribeToNotifications();
    this.openDefaultBoUrl();
    this.changePhonePosition();
    this.changeContainerTitle();
    this.firebaseRegistrationToken();
    this.subscribeVMReaderUpdate();
    this.subscribeMissedCallStatus();
  }

  private subscribeNewSms(): void {
    this.wsrpc.subscribe('NEW_SMS', undefined,
      async (sms_data: {
        message: 'NEW_SMS',
        user_ids?: number[],
        data: {
          sms_id: number,
          text: string
        }
      }) => {
        // this.loggerService.log('New SMS received', sms_data);

        const data = !_.isEmpty(sms_data) && sms_data.data;
        if (_.isEmpty(data) && !data.sms_id) {
          return;
        }

        this.windowService.bringToFront();
        this.messageService.broadcastNewSms();
        this.sms.updateTotalConversationUnreadSms();

        const smsId = data.sms_id;
        const sms: SmsMessage = await this.sms.getSmsMessageInfo(smsId);
        const client_info = sms.client_name || PhoneNumberService.getInstance().getDisplayNumber(sms.remote_number);
        const title = _ti('sms.notification.new_message', { sender_number: client_info });
        const viewActionText = _ti('public.view');
        this.toast.show(sms_data.data.text, title, ToastStyle.INFO, ToastService.DURATION_SHORT, viewActionText, async () => {
          const dlg: SmsDialog = await this.sms.getSmsConversationInfo(sms.dialog_id);
          this.smsDialogService.detailConversation(dlg);
        });
      }
    );
  }

  private subscribeMissedCallStatus(): void {
    // this.loggerService.log('Subscribe WS MISSED_CALL_UPDATE');
    this.wsrpc.subscribe('MISSED_CALL_UPDATE', undefined,
      (data: {
        message: 'MISSED_CALL_UPDATE',
        enterprise_id?: number,
        data: {
          call_id: number,
          owner_id: number,
          recall_state: number,
          last_outbound_user_id: number,
          recall_request_status: number,
        }
      }) => {
        // this.loggerService.log('Update missed call', data);
        this.messageService.broadcastMissedCallStatus(data);
      }
    );
  }

  private subscribeVMReaderUpdate(): void {
    // this.loggerService.log('Subscribe WS VM_READER_UPDATE');
    this.wsrpc.subscribe('VM_READER_UPDATE', undefined,
      (data: VMReaderDataMsg) => {
        // this.loggerService.log('Update voicemail reader', data);
        this.messageService.broadcastVMReaderUpdate(data);
      }
    );
  }

  private subscribeStatusSms(): void {
    this.wsrpc.subscribe('SMS_STATUS', undefined,
      async (sms_data: {
        message: 'SMS_STATUS',
        user_ids?: number[],
        data: {
          sms_id: number
        }
      }) => {
        // this.loggerService.log('Update status SMS', sms_data);

        const smsId = !_.isEmpty(sms_data) && sms_data.data && sms_data.data.sms_id;
        if (!smsId) {
          return;
        }

        const sms: SmsMessage = await this.sms.getSmsMessageInfo(smsId);
        this.messageService.broadcastStatusSms(sms);
      }
    );
  }

  private subscribeQualificationDone(): void {
    this.wsrpc.subscribe('QUALIFICATION_DONE', undefined,
      async (call_data: {
        message: 'QUALIFICATION_DONE',
        user_ids?: number[],
        data: {
          callqueue_session_id: number,
          type: 'call'|'audio',
        }
      }) => {
        // this.loggerService.log('Update qualification done', call_data);

        const callqueueSessionId = !_.isEmpty(call_data) && call_data.data && call_data.data.callqueue_session_id;
        if (!callqueueSessionId) {
          this.loggerService.warn('No call-queue-session id responsed from qualification-done');
          return;
        }

        this.messageService.broadcastQualificationDone(call_data.data);
      }
    );
  }

  private subscribeDisconnectAgent(): void {
    this.wsrpc.subscribe('DISCONNECTION_REQUEST', undefined,
      async (disconnectData: {
        message: string;
        data: {
          sender: number;
        }
      }) => {
        // this.loggerService.log('Disconnect Agent', disconnectData);
        await this.auth.logout();
      }
    );
  }

  subscribeToNotifications(): void {
    if (environment.webpushPublicKey) {
      this.swPush.requestSubscription({
        serverPublicKey: environment.webpushPublicKey,
      }).then((sub: PushSubscription) => {
        this.webpushService.register({ appName: 'phone', sub: sub.toJSON() });
      }).catch(err => {
        this.loggerService.error('Could not subscribe to notifications', err);
      });
    }
  }

  openDefaultBoUrl(): void {
    const myEnterprise = EnterpriseRepository.getInstance<EnterpriseRepository>().getMyEnterprise();
    if (!myEnterprise.default_bo_url) {
      return;
    }

    if (window.self !== window.top) {
      window.parent.postMessage({ function: 'graam_post_msg.open_bo_url', url: myEnterprise.default_bo_url }, this.settings.root_url);
    }
  }

  changePhonePosition(): void {
    const userParams = this.configService.getUserParams();
    if (window.self !== window.top && userParams.open_bo_on_call && userParams.iframe_bo_position) {
      window.parent.postMessage({ function: 'graam_post_msg.phone_change_position', position: userParams.iframe_bo_position }, this.settings.root_url);
    }
  }

  changeContainerTitle(): void {
    const myEnterprise = EnterpriseRepository.getInstance<EnterpriseRepository>().getMyEnterprise();
    if (!myEnterprise) {
      return;
    }
    if (window.self !== window.top && myEnterprise.name) {
      window.parent.postMessage({ function: 'graam_post_msg.change_title', enterprise_name: myEnterprise.name }, this.settings.root_url);
    }
  }

  firebaseRegistrationToken(): void {
    if (window['Android']) {
      const currentUser = this.auth.getUser();
      window['Android'].firebaseRegistrationToken(currentUser.id);
    }
  }

  getEnterpriseParams(): EnterpriseParamDict {
    return this.configService.getEnterpriseParams();
  }
}
